import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeft, Plus, Package, Search, AlertTriangle,
  TrendingDown, TrendingUp, Minus, Edit, Trash2, History
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Inventory = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [stats, setStats] = useState({ total_items: 0, low_stock_count: 0, total_value: 0 });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showItemModal, setShowItemModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    name: '',
    category: 'materials',
    description: '',
    sku: '',
    current_stock: 0,
    min_stock: 10,
    max_stock: 100,
    unit: 'pcs',
    cost_per_unit: 0,
    supplier: '',
    location: ''
  });
  const [adjustForm, setAdjustForm] = useState({
    type: 'add',
    quantity: 0,
    reason: ''
  });

  useEffect(() => {
    loadData();
  }, [categoryFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = categoryFilter !== 'all' ? { category: categoryFilter } : {};
      const [itemsRes, statsRes, catsRes] = await Promise.all([
        axios.get(`${API}/inventory`, { params }),
        axios.get(`${API}/inventory/stats`),
        axios.get(`${API}/inventory/categories`)
      ]);
      
      setInventory(itemsRes.data.items || []);
      setStats(statsRes.data);
      setCategories(catsRes.data.categories || []);
    } catch (error) {
      console.error('Failed to load inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenItemModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setItemForm({
        name: item.name || '',
        category: item.category || 'materials',
        description: item.description || '',
        sku: item.sku || '',
        current_stock: item.current_stock || 0,
        min_stock: item.min_stock || 10,
        max_stock: item.max_stock || 100,
        unit: item.unit || 'pcs',
        cost_per_unit: item.cost_per_unit || 0,
        supplier: item.supplier || '',
        location: item.location || ''
      });
    } else {
      setEditingItem(null);
      setItemForm({
        name: '',
        category: 'materials',
        description: '',
        sku: '',
        current_stock: 0,
        min_stock: 10,
        max_stock: 100,
        unit: 'pcs',
        cost_per_unit: 0,
        supplier: '',
        location: ''
      });
    }
    setShowItemModal(true);
  };

  const handleSaveItem = async () => {
    if (!itemForm.name) {
      toast.error('Please enter item name');
      return;
    }

    try {
      if (editingItem) {
        await axios.put(`${API}/inventory/${editingItem.id}`, itemForm);
        toast.success('Item updated');
      } else {
        await axios.post(`${API}/inventory`, itemForm);
        toast.success('Item created');
      }
      setShowItemModal(false);
      loadData();
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`${API}/inventory/${itemId}`);
      toast.success('Item deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleOpenAdjustModal = (item, type) => {
    setSelectedItem(item);
    setAdjustForm({ type, quantity: 0, reason: '' });
    setShowAdjustModal(true);
  };

  const handleAdjustStock = async () => {
    if (adjustForm.quantity <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    try {
      await axios.post(`${API}/inventory/${selectedItem.id}/adjust`, adjustForm);
      toast.success(`Stock ${adjustForm.type === 'add' ? 'added' : 'used'} successfully`);
      setShowAdjustModal(false);
      loadData();
    } catch (error) {
      toast.error('Failed to adjust stock');
    }
  };

  const getStockLevel = (item) => {
    if (item.current_stock <= item.min_stock) return 'low';
    const percentage = (item.current_stock / item.max_stock) * 100;
    if (percentage > 70) return 'high';
    return 'medium';
  };

  const getStockBadge = (level) => {
    const variants = {
      low: 'destructive',
      medium: 'secondary',
      high: 'default'
    };
    return variants[level];
  };

  const filteredInventory = inventory.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.sku && item.sku.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="p-6 space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <Button onClick={() => navigate('/tools')} variant="outline" size="sm">
              <ArrowLeft size={16} className="mr-2" />
              Back to Tools
            </Button>
          </div>
          <h1 className="text-2xl font-bold text-foreground">Inventory Management</h1>
          <p className="text-muted-foreground mt-1">Material & Equipment Tracking</p>
        </div>
        <Button onClick={() => handleOpenItemModal()} data-testid="add-item-btn">
          <Plus size={16} className="mr-2" />
          Add Item
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Package size={24} className="mx-auto text-primary mb-2" />
              <p className="text-3xl font-bold text-foreground">{stats.total_items}</p>
              <p className="text-xs text-muted-foreground">Total Items</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertTriangle size={24} className="mx-auto text-red-500 mb-2" />
              <p className="text-3xl font-bold text-red-500">{stats.low_stock_count}</p>
              <p className="text-xs text-muted-foreground">Low Stock</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <TrendingUp size={24} className="mx-auto text-green-500 mb-2" />
              <p className="text-3xl font-bold text-green-500">${stats.total_value?.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Inventory Value</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
              <Input
                placeholder="Search inventory..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 flex-wrap">
        <Button
          variant={categoryFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setCategoryFilter('all')}
        >
          All
        </Button>
        {categories.map((cat) => (
          <Button
            key={cat.id}
            variant={categoryFilter === cat.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCategoryFilter(cat.id)}
          >
            {cat.name}
          </Button>
        ))}
      </div>

      {/* Inventory List */}
      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Loading inventory...
          </CardContent>
        </Card>
      ) : filteredInventory.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-2">No inventory items found</p>
            <Button onClick={() => handleOpenItemModal()}>
              <Plus size={16} className="mr-2" />
              Add Your First Item
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredInventory.map((item) => {
            const stockLevel = getStockLevel(item);
            const stockPercentage = (item.current_stock / item.max_stock) * 100;

            return (
              <Card key={item.id} className="hover:border-primary transition-all">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <CardTitle className="text-lg text-foreground">{item.name}</CardTitle>
                        <Badge variant={getStockBadge(stockLevel)}>
                          {stockLevel.toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {item.category} • {item.location || 'No location'}
                        {item.sku && ` • SKU: ${item.sku}`}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleOpenItemModal(item)}>
                        <Edit size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteItem(item.id)}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Current Stock</p>
                        <p className="text-2xl font-bold text-foreground">
                          {item.current_stock} {item.unit}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Min / Max</p>
                        <p className="font-mono text-sm text-foreground">
                          {item.min_stock} / {item.max_stock} {item.unit}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Cost per Unit</p>
                        <p className="font-mono font-bold text-foreground">${item.cost_per_unit?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Total Value</p>
                        <p className="font-mono font-bold text-primary">
                          ${(item.current_stock * item.cost_per_unit).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <div>
                      <Progress value={Math.min(stockPercentage, 100)} className="h-2" />
                      <p className="text-xs text-muted-foreground mt-1">
                        {stockPercentage.toFixed(0)}% of maximum capacity
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleOpenAdjustModal(item, 'add')}>
                        <Plus size={14} className="mr-2" />
                        Add Stock
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleOpenAdjustModal(item, 'use')}>
                        <Minus size={14} className="mr-2" />
                        Use Stock
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Item Modal */}
      <Dialog open={showItemModal} onOpenChange={setShowItemModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Item Name *</Label>
              <Input
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                placeholder="e.g., Steel Sheet 4x8"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={itemForm.category} onValueChange={(v) => setItemForm({ ...itemForm, category: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>SKU</Label>
                <Input
                  value={itemForm.sku}
                  onChange={(e) => setItemForm({ ...itemForm, sku: e.target.value })}
                  placeholder="SKU-001"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Current Stock</Label>
                <Input
                  type="number"
                  value={itemForm.current_stock}
                  onChange={(e) => setItemForm({ ...itemForm, current_stock: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Min Stock</Label>
                <Input
                  type="number"
                  value={itemForm.min_stock}
                  onChange={(e) => setItemForm({ ...itemForm, min_stock: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Max Stock</Label>
                <Input
                  type="number"
                  value={itemForm.max_stock}
                  onChange={(e) => setItemForm({ ...itemForm, max_stock: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unit</Label>
                <Select value={itemForm.unit} onValueChange={(v) => setItemForm({ ...itemForm, unit: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pcs">Pieces</SelectItem>
                    <SelectItem value="lbs">Pounds</SelectItem>
                    <SelectItem value="kg">Kilograms</SelectItem>
                    <SelectItem value="sheets">Sheets</SelectItem>
                    <SelectItem value="feet">Feet</SelectItem>
                    <SelectItem value="meters">Meters</SelectItem>
                    <SelectItem value="gallons">Gallons</SelectItem>
                    <SelectItem value="liters">Liters</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cost per Unit ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={itemForm.cost_per_unit}
                  onChange={(e) => setItemForm({ ...itemForm, cost_per_unit: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Supplier</Label>
                <Input
                  value={itemForm.supplier}
                  onChange={(e) => setItemForm({ ...itemForm, supplier: e.target.value })}
                  placeholder="Supplier name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Location</Label>
                <Input
                  value={itemForm.location}
                  onChange={(e) => setItemForm({ ...itemForm, location: e.target.value })}
                  placeholder="e.g., Bay A"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemModal(false)}>Cancel</Button>
            <Button onClick={handleSaveItem}>{editingItem ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Stock Modal */}
      <Dialog open={showAdjustModal} onOpenChange={setShowAdjustModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {adjustForm.type === 'add' ? 'Add Stock' : 'Use Stock'}
            </DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {selectedItem.name} • Current: {selectedItem.current_stock} {selectedItem.unit}
              </p>
              <div>
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  value={adjustForm.quantity}
                  onChange={(e) => setAdjustForm({ ...adjustForm, quantity: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Reason</Label>
                <Input
                  value={adjustForm.reason}
                  onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                  placeholder="e.g., Project #123"
                  className="mt-1"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjustModal(false)}>Cancel</Button>
            <Button onClick={handleAdjustStock}>
              {adjustForm.type === 'add' ? 'Add' : 'Use'} Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inventory;
